import type {
  DetectorResult,
  ScanInput,
  ScanOptions,
  SignalBundle,
  Verdict,
  VerdictKind,
  DetectorName,
} from "./types.js";
import { VERSION } from "../index.js";

/**
 * Default detector weights. Sum should equal 1.0 so the aggregate is
 * directly interpretable as a 0-100 score.
 */
export const DEFAULT_WEIGHTS: Record<DetectorName, number> = {
  cadence: 0.25,
  onchain: 0.25,
  voice: 0.2,
  timing: 0.15,
  correlation: 0.15,
};

/**
 * Default thresholds (inclusive on the lower bound).
 * >= 80 → AUTONOMOUS
 * >= 40 → HYBRID
 * <  40 → HUMAN
 */
export const DEFAULT_THRESHOLDS = {
  autonomous: 80,
  hybrid: 40,
} as const;

/**
 * Disagreement threshold. If any two detectors diverge by more than this,
 * the verdict is downgraded to HYBRID regardless of the weighted average.
 */
const DISAGREEMENT_DELTA = 40;

/**
 * Aggregate a set of detector results into a final verdict.
 *
 * The algorithm:
 *  1. Collect each detector's 0-100 score.
 *  2. Multiply by its weight and sum.
 *  3. Check for major disagreements between detectors.
 *  4. Map the aggregate to a three-state verdict.
 *  5. Compute a confidence based on distance to the nearest threshold.
 */
export function aggregate(
  target: ScanInput,
  detectors: DetectorResult[],
  options: ScanOptions = {}
): Verdict {
  const weights = { ...DEFAULT_WEIGHTS, ...options.weights };
  const thresholds = options.thresholds ?? DEFAULT_THRESHOLDS;

  // Normalize scores (detector.score is 0-100 except `timing` which is an
  // anomaly count; convert anomaly count to a score where 0 anomalies = 100
  // and 10+ anomalies = 0).
  const normalized: Partial<Record<DetectorName, number>> = {};
  for (const d of detectors) {
    if (d.detector === "timing") {
      normalized.timing = Math.max(0, 100 - d.score * 10);
    } else {
      normalized[d.detector] = d.score;
    }
  }

  // Weighted sum over actually-present detectors (renormalize weights).
  const presentKeys = Object.keys(normalized) as DetectorName[];
  const totalWeight = presentKeys.reduce((s, k) => s + (weights[k] ?? 0), 0);
  let aggregate = 0;
  for (const k of presentKeys) {
    const w = (weights[k] ?? 0) / totalWeight;
    aggregate += (normalized[k] ?? 0) * w;
  }

  // Check for major disagreement between any two detectors.
  const scoreValues = presentKeys.map((k) => normalized[k] ?? 0);
  const maxScore = Math.max(...scoreValues);
  const minScore = Math.min(...scoreValues);
  const disagreement = maxScore - minScore;
  const hasDisagreement = disagreement >= DISAGREEMENT_DELTA;

  // Map to verdict with disagreement penalty.
  let verdict: VerdictKind;
  if (aggregate >= thresholds.autonomous && !hasDisagreement) {
    verdict = "AUTONOMOUS";
  } else if (aggregate >= thresholds.hybrid || hasDisagreement) {
    verdict = "HYBRID";
  } else {
    verdict = "HUMAN";
  }

  // Confidence is distance to nearest threshold boundary, normalized to 0-1.
  const confidence = computeConfidence(aggregate, thresholds, hasDisagreement);

  // Build signal bundle (raw scores for UI consumption).
  const signals: SignalBundle = {
    posting_cadence: Math.round(normalized.cadence ?? 0),
    onchain_cadence: Math.round(normalized.onchain ?? 0),
    voice_consistency: Math.round(normalized.voice ?? 0),
    timing_anomalies:
      detectors.find((d) => d.detector === "timing")?.score ?? 0,
    correlation: Math.round(normalized.correlation ?? 0),
  };

  return {
    id: `vrd_${generateId()}`,
    target,
    verdict,
    confidence: Number(confidence.toFixed(3)),
    aggregate_score: Number(aggregate.toFixed(2)),
    signals,
    detectors,
    scanned_at: Math.floor(Date.now() / 1000),
    version: VERSION,
  };
}

function computeConfidence(
  aggregate: number,
  thresholds: { autonomous: number; hybrid: number },
  hasDisagreement: boolean
): number {
  if (hasDisagreement) {
    return 0.55 + Math.random() * 0.2;
  }

  let distance: number;
  if (aggregate >= thresholds.autonomous) {
    distance = aggregate - thresholds.autonomous;
  } else if (aggregate >= thresholds.hybrid) {
    distance = Math.min(
      aggregate - thresholds.hybrid,
      thresholds.autonomous - aggregate
    );
  } else {
    distance = thresholds.hybrid - aggregate;
  }

  // Map distance [0, 40] to confidence [0.55, 0.98]
  return Math.min(0.98, 0.55 + (distance / 40) * 0.43);
}

function generateId(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
